import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Tooltip,
  Typography,
  useTheme,
  type Theme,
} from '@mui/material'
import { DateTime } from 'luxon'
import { connectionStateColors } from '../../theme'
import { FetchFromBackoffice } from '../../utils/request'
import { DataLoadingText } from '../../components/atoms/text/dataLoadingText'

const BUCKET_MINUTES = 15

function floorTo15Minutes(dt: DateTime): DateTime {
  const d = dt.set({ second: 0, millisecond: 0 })
  const flooredMinute = Math.floor(d.minute / BUCKET_MINUTES) * BUCKET_MINUTES
  return d.set({ minute: flooredMinute })
}

type HealthBucketValue = 0 | 0.5 | 1

type HealthBucketCell = HealthBucketValue | null

type HealthGridRow = {
  hubId: string
  label: string
  buckets: HealthBucketCell[]
}

type HealthGridData = {
  from: DateTime
  to: DateTime
  rows: HealthGridRow[]
}

type MachineOnlineHistoryMap = Record<string, MachineOnlineHistory>

type MachineOnlineHistory = {
  serialNumber: string
  timestamps: MachineTimestamps[]
}

type MachineTimestamps = {
  time: string
  isProblematic: number
  statusLabel?: string
  serialNumber?: string
}

function transformToHealthGridData(
  hubs: MachineOnlineHistory[],
  rangeStart: DateTime,
  rangeEnd: DateTime
): HealthGridData {
  const from = floorTo15Minutes(rangeStart)
  const to = floorTo15Minutes(rangeEnd)
  const bucketCount = getBucketCount(from, to)
  if (!hubs || hubs.length === 0) return { from, to, rows: [] }

  const rows: HealthGridRow[] = hubs.map((hub) => {
    const buckets: HealthBucketCell[] = Array.from({ length: bucketCount }, () => null)
    for (const ts of hub.timestamps ?? []) {
      const t = DateTime.fromISO(ts.time).toLocal()
      if (!t.isValid) continue
      const minutesFromStart = t.diff(from, 'minutes').minutes
      const bucketIndex = Math.floor(minutesFromStart / BUCKET_MINUTES)
      if (bucketIndex >= 0 && bucketIndex < bucketCount) {
        const v = ts.isProblematic
        buckets[bucketIndex] = (v === 1 ? 1 : v === 0.5 ? 0.5 : 0) as HealthBucketValue
      }
    }
    return { hubId: hub.serialNumber, label: hub.serialNumber, buckets }
  })
  return { from, to, rows }
}

function getBucketCount(from: DateTime, to: DateTime): number {
  const durationMinutes = to.diff(from, 'minutes').minutes
  return Math.max(0, Math.ceil(durationMinutes / BUCKET_MINUTES))
}

function getBucketStartTime(from: DateTime, bucketIndex: number): DateTime {
  return from.plus({ minutes: bucketIndex * BUCKET_MINUTES })
}

function getStatusLabel(value: HealthBucketCell): string {
  if (value === 1) return 'Down'
  if (value === 0) return 'Healthy'
  if (value === 0.5) return 'Warning'
  return 'No data'
}

function getColorForValue(value: HealthBucketCell, theme: Theme): string {
  if (getStatusLabel(value) === 'Healthy') return connectionStateColors.connected
  if (getStatusLabel(value) === 'Warning') return connectionStateColors.disconnected
  if (getStatusLabel(value) === 'Down') return theme.palette.error.main
  return theme.palette.action.disabledBackground
}

const ROW_HEIGHT_PX = 20
const LABEL_COLUMN_WIDTH_PX = 120
const LABEL_EVERY_N_BUCKETS = 8

const LegendItem =({ label, color }: { label: string; color: string }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: 0.5,
          bgcolor: color,
        }}
      />
      <Typography variant="body2" component="span">
        {label}
      </Typography>
    </Box>
  )
}

export const HubHealthTable = ({showAll}: {showAll: boolean}): React.ReactElement => {
  const theme = useTheme() as Theme
  const [statusData, setStatusData] = useState<MachineOnlineHistory[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const now = DateTime.now()
  const stop = now
  const start = now.minus({ hours: 24 })

  useEffect(() => {
    fetchStatusData()
  }, [showAll])

  const fetchStatusData = async () => {
    setIsLoading(true)
    try {
      const startIso = start.toISO()
      const stopIso = stop.toISO()
      const raw = await FetchFromBackoffice<MachineOnlineHistoryMap>(
        `machine-online-history?start=${encodeURIComponent(startIso)}&stop=${encodeURIComponent(stopIso)}${showAll ?  "" : "&param=onlyActivated"}`
      )
      const data = Object.values(raw ?? {})
      setStatusData(data)
    } catch (error) {
      console.error('Error fetching machine online history:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const gridData = useMemo(
    () => transformToHealthGridData(statusData, start, stop),
    [statusData, start, stop]
  )

  const bucketCount = gridData.rows[0]?.buckets.length ?? 0
  const from = gridData.from

  const timeLabels = useMemo(() => {
    const labels: { index: number; label: string; title: string }[] = []
    for (let i = 0; i < bucketCount; i += LABEL_EVERY_N_BUCKETS) {
      const t = getBucketStartTime(from, i)
      const tEnd = t.plus({ hours: 2 })
      labels.push({
        index: i,
        label: t.toFormat('HH:mm'),
        title: `${t.toFormat('HH:mm')} – ${tEnd.toFormat('HH:mm')}`,
      })
    }
    return labels
  }, [from, bucketCount])

  if (isLoading) {
    return (
      <Box sx={{ py: 2 }}>
        <DataLoadingText showSpinner text="Loading hub health…" />
      </Box>
    )
  }

  if (gridData.rows.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography color="text.secondary">No hub data to display.</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5}}>
          <LegendItem label="Healthy" color={connectionStateColors.connected} />
          <LegendItem label="Warning" color={connectionStateColors.disconnected} />
          <LegendItem label="Down" color={theme.palette.error.main} />
          <LegendItem label="No data" color={theme.palette.action.disabledBackground} />
        </Box>
      </Box>

      <Box sx={{ width: '100%', overflow: 'visible' }}>
        <Box
          role="grid"
          aria-label="Hub health timeline"
          sx={{
            display: 'grid',
            gridTemplateColumns: `${LABEL_COLUMN_WIDTH_PX}px repeat(${bucketCount}, minmax(0, 1fr))`,
            gridTemplateRows: `auto repeat(${gridData.rows.length}, ${ROW_HEIGHT_PX}px)`,
            width: '100%',
            overflow: 'visible',
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              left: 0,
              zIndex: 1,
              padding: '4px 8px',
              fontWeight: 'bold',
              fontSize: 12,
            }}
          >
            Hub
          </Box>
          {Array.from({ length: bucketCount }, (_, i) => {
            const labelEntry = timeLabels.find((l) => l.index === i)
            return (
              <Box
                key={`h-${i}`}
                title={labelEntry?.title}
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  padding: '0 4px 0 0',
                  fontSize: 10,
                  fontWeight: 'normal',
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'flex-end',
                  overflow: 'visible',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    overflow: 'visible',
                    position: 'relative',
                    zIndex: 1,
                    paddingRight: '2em',
                  }}
                >
                  {labelEntry ? labelEntry.label : ''}
                </span>
              </Box>
            )
          })}
          {gridData.rows.map((row) => (
            <React.Fragment key={row.hubId}>
              <Box
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  padding: '2px 8px',
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                {row.label}
              </Box>
              {row.buckets.map((value, i) => {
                const bucketStart = getBucketStartTime(from, i)
                const bucketEnd = bucketStart.plus({
                  minutes: BUCKET_MINUTES,
                })
                const status = getStatusLabel(value)
                const color = getColorForValue(value, theme)
                return (
                  <Tooltip
                    key={i}
                    title={`${bucketStart.toFormat('HH:mm')} – ${bucketEnd.toFormat('HH:mm')} · ${status}`}
                    placement="top"
                    slotProps={{
                      popper: {
                        sx: {
                          '& .MuiTooltip-tooltip': {
                            backgroundColor: 'background.paper',
                            color: 'text.primary',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 0,
                            padding: '8px 12px',
                            boxShadow: 1,
                            fontSize: 12,
                            fontWeight: 'normal'
                          },
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        backgroundColor: color,
                        borderLeft: 1,
                        borderBottom: 1,
                        borderColor: 'white',
                        borderRadius: '3px',
                        '&:hover': { opacity: 0.65 },
                      }}
                    />
                  </Tooltip>
                )
              })}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  )
}
